import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Cog, Shield, Zap, Award, Users, ArrowRight, Quote } from 'lucide-react';
import VitrineLayout from '../../components/layouts/VitrineLayout';
import {
  COPPER, COPPER_LIGHT, CREAM, DARK_BG, DARK_BG_2,
  DISPLAY_FF, ITALIC_FF, GRAIN_BG, GLASS_STYLE, COPPER_BTN_STYLE,
  CopperHalo, Eyebrow, PageHero, Shimmer,
} from '../../components/vitrine/theme';

const craftsmanImage = "/atelier-granipal-v2.jpg";

const steps = [
  { icon: Target, title: 'Sélection',  description: 'Sélection des matières chez nos partenaires fournisseurs — les plus belles pierres naturelles et céramiques.' },
  { icon: Cog,    title: 'Façonnage',  description: "Façonnage manuel et précision millimétrique avec nos machines et l'expertise de nos marbriers." },
  { icon: Shield, title: 'Finition',   description: 'Polissage, brossage, flammage — chaque finition est réalisée à la main par nos marbriers.' },
  { icon: Zap,    title: 'Pose',       description: 'Livraison et installation par notre équipe, généralement sous 2 à 3 semaines de délais.' },
];

const values = [
  { icon: Award, title: 'Excellence',  description: 'Aucun compromis sur la qualité des matériaux et des finitions.' },
  { icon: Users, title: 'Proximité',   description: 'Un interlocuteur dédié vous accompagne tout au long du projet.' },
  { icon: Shield,title: 'Fiabilité',   description: "Plus de 25 ans d'expérience et des centaines de projets livrés." },
];

export default function SavoirFairePage() {
  return (
    <VitrineLayout>
      <PageHero
        eyebrow="Notre expertise"
        title="Savoir-"
        titleEm="faire"
        description="Plus de 25 ans d'excellence artisanale au service de vos projets les plus exigeants."
        testid="savoir-hero"
      />

      {/* "L'art de la pierre" — atelier image as BACKGROUND.
          The photo fills the entire section. A cinematic gradient ensures readability.
          Heritage text + stats float on top in a premium glass composition. */}
      <section className="relative overflow-hidden" style={{ background: DARK_BG }} data-testid="savoir-atelier">
        {/* BACKGROUND atelier image — fills the section */}
        <img
          src={craftsmanImage}
          alt="Atelier Granipal — Vigneux-sur-Seine"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: 'saturate(95%) brightness(0.78) contrast(1.05)',
            objectPosition: 'center center',
          }}
        />
        {/* Cinematic dual gradient — darker on the LEFT (text zone), softer on right */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(95deg, rgba(18,11,7,0.92) 0%, rgba(18,11,7,0.78) 35%, rgba(18,11,7,0.40) 65%, rgba(18,11,7,0.30) 100%),' +
              'linear-gradient(180deg, rgba(18,11,7,0.30) 0%, rgba(18,11,7,0) 25%, rgba(18,11,7,0) 75%, rgba(18,11,7,0.55) 100%)',
          }}
        />
        <CopperHalo x="20%" y="30%" size={900} opacity={0.18} />
        <CopperHalo x="80%" y="70%" size={800} opacity={0.10} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[500px] lg:min-h-[620px]">
            {/* LEFT — Heritage card + stats. Glassmorphism on top of the image background */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } }}
              className="lg:col-span-6 flex flex-col gap-5 lg:gap-6"
            >
              {/* Heritage glass card — large, premium */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } }}
                className="relative px-7 py-8 lg:px-9 lg:py-10"
                style={{
                  ...GLASS_STYLE,
                  background: 'linear-gradient(160deg, rgba(20,11,7,0.55) 0%, rgba(18,11,7,0.35) 100%)',
                  backdropFilter: 'blur(22px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(22px) saturate(160%)',
                  border: '1px solid rgba(212,168,120,0.22)',
                  boxShadow: '0 30px 80px -30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(245,230,208,0.05)',
                }}
              >
                <Eyebrow>L'art de la pierre</Eyebrow>
                <Quote className="w-8 h-8 mb-4 opacity-75" style={{ color: COPPER_LIGHT }} />
                <h2
                  className="leading-[1.08] mb-5"
                  style={{
                    fontFamily: DISPLAY_FF,
                    fontSize: 'clamp(1.7rem, 3vw, 2.5rem)',
                    fontWeight: 400,
                    color: CREAM,
                    letterSpacing: '0.015em',
                    textShadow: '0 2px 12px rgba(0,0,0,0.45)',
                  }}
                >
                  Fondée en <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT, fontWeight: 400 }}>1997</em>, une référence pour la pierre naturelle.
                </h2>
                <p className="text-[14px] lg:text-[15px] leading-relaxed font-light" style={{ color: 'rgba(245,230,208,0.88)' }}>
                  Notre atelier de Vigneux-sur-Seine réunit les meilleurs artisans, formés aux
                  techniques traditionnelles et équipés des outils de précision les plus modernes.
                </p>
              </motion.div>

              {/* 3 stat cards in a row — compact, glassmorphism, copper accents */}
              <div className="grid grid-cols-3 gap-2.5 lg:gap-3">
                {[
                  { value: '25+',  label: "Années" },
                  { value: '500+', label: 'Projets' },
                  { value: '100%', label: 'Sur-mesure' },
                ].map((s, i) => (
                  <motion.div
                    key={s.label}
                    variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } } }}
                    className="relative px-3 py-4 lg:px-5 lg:py-5 text-center"
                    style={{
                      background: 'linear-gradient(160deg, rgba(184,137,90,0.18) 0%, rgba(20,11,7,0.45) 100%)',
                      backdropFilter: 'blur(20px) saturate(160%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                      border: '1px solid rgba(212,168,120,0.32)',
                      boxShadow: 'inset 0 1px 0 rgba(245,230,208,0.06)',
                    }}
                  >
                    <div style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(1.6rem, 2.8vw, 2.2rem)', color: COPPER_LIGHT, letterSpacing: '0.01em', lineHeight: 1 }}>
                      {s.value}
                    </div>
                    <div className="mt-2 text-[9.5px] lg:text-[10px] uppercase tracking-[0.32em]" style={{ color: 'rgba(245,230,208,0.85)' }}>{s.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT — Discreet brand mark / location detail (col-span-6) */}
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-6 flex flex-col items-start lg:items-end justify-end gap-4 lg:gap-5"
            >
              {/* Atelier marker — top-right floating tag */}
              <div className="inline-flex items-center gap-3 px-4 py-2"
                style={{
                  background: 'rgba(20,11,7,0.55)',
                  border: '1px solid rgba(212,168,120,0.38)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}
              >
                <span aria-hidden className="block w-2 h-2 rounded-full" style={{ background: COPPER_LIGHT, boxShadow: `0 0 10px ${COPPER_LIGHT}` }} />
                <span className="text-[10px] uppercase tracking-[0.40em]" style={{ color: COPPER_LIGHT }}>
                  Atelier · Vigneux-sur-Seine
                </span>
              </div>
              {/* Address line, with copper bullet */}
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.36em] mb-1.5" style={{ color: 'rgba(245,230,208,0.55)' }}>Adresse</div>
                <div style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(1.1rem, 1.8vw, 1.4rem)', color: CREAM, lineHeight: 1.3, textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
                  6 Rue de la porte écluse
                </div>
                <div style={{ fontFamily: DISPLAY_FF, fontStyle: 'italic', fontSize: 'clamp(1rem, 1.6vw, 1.25rem)', color: COPPER_LIGHT, letterSpacing: '0.01em', textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
                  91270 Vigneux-sur-Seine
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps — glass cards (top half of pair) */}
      <section className="relative pt-20 pb-12 lg:pt-24 lg:pb-14 overflow-hidden" style={{ background: DARK_BG_2 }}>
        <CopperHalo x="20%" y="50%" size={900} opacity={0.10} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.7 }} className="text-center mb-12 lg:mb-14">
            <Eyebrow centered>Notre processus</Eyebrow>
            <h2 style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 400, color: CREAM, letterSpacing: '0.02em' }}>
              De la <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>matière</em> à votre intérieur
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 18, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.3 }}
                className="relative px-7 py-9 group"
                style={GLASS_STYLE}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'rgba(184,137,90,0.12)', border: '1px solid rgba(212,168,120,0.40)' }}>
                    <step.icon className="w-6 h-6" style={{ color: COPPER_LIGHT }} />
                  </div>
                  <span style={{ fontFamily: DISPLAY_FF, fontSize: '2.4rem', color: 'rgba(212,168,120,0.18)', letterSpacing: '0.02em', lineHeight: 1 }}>
                    0{i + 1}
                  </span>
                </div>
                <h3 style={{ fontFamily: DISPLAY_FF, fontSize: '1.4rem', fontWeight: 400, color: CREAM, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                  {step.title}
                </h3>
                <p className="mt-3 text-[13px] font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.6)' }}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider — elegant copper line bridges Process → Values */}
      <div className="relative" style={{ background: DARK_BG_2 }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10">
          <div className="flex items-center justify-center gap-5">
            <span aria-hidden className="block h-px flex-1 max-w-[200px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,120,0.5))' }} />
            <span className="text-[10px] uppercase tracking-[0.5em]" style={{ color: 'rgba(212,168,120,0.65)' }}>·</span>
            <span aria-hidden className="block h-px flex-1 max-w-[200px]" style={{ background: 'linear-gradient(90deg, rgba(212,168,120,0.5), transparent)' }} />
          </div>
        </div>
      </div>

      {/* Values — centered, distinct copper-tinted dark BG, immediately follows process */}
      <section className="relative pt-12 pb-24 lg:pt-14 lg:pb-32 overflow-hidden" style={{ background: DARK_BG_2 }}>
        <CopperHalo x="70%" y="40%" size={800} opacity={0.14} />
        <CopperHalo x="20%" y="70%" size={700} opacity={0.10} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.7 }} className="text-center mb-12 lg:mb-14">
            <Eyebrow centered>Nos valeurs</Eyebrow>
            <h2 style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 400, color: CREAM, letterSpacing: '0.02em' }}>
              Ce qui <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>nous anime</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.3 }}
                className="relative px-7 py-10 text-center"
                style={{
                  ...GLASS_STYLE,
                  // Slightly more cuivré tint so Values feels distinct from Process
                  background: 'linear-gradient(160deg, rgba(184,137,90,0.10) 0%, rgba(245,230,208,0.03) 50%, rgba(20,11,7,0.25) 100%)',
                }}
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full" style={{ background: 'rgba(184,137,90,0.18)', border: '1px solid rgba(212,168,120,0.55)' }}>
                  <value.icon className="w-7 h-7" style={{ color: COPPER_LIGHT }} />
                </div>
                <h3 style={{ fontFamily: DISPLAY_FF, fontSize: '1.4rem', fontWeight: 400, color: CREAM, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                  {value.title}
                </h3>
                <p className="mt-3 text-[13px] font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.6)' }}>
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32 overflow-hidden" style={{ background: DARK_BG_2 }}>
        <CopperHalo x="50%" y="50%" size={1100} opacity={0.18} />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="relative px-8 py-14 lg:py-20 lg:px-16 overflow-hidden" style={GLASS_STYLE}>
            <Eyebrow centered>Votre projet</Eyebrow>
            <h2 className="mb-6" style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 400, color: CREAM, letterSpacing: '0.02em', lineHeight: 1.15 }}>
              Vous avez un <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>projet</em> ?
            </h2>
            <p className="mb-10 max-w-md mx-auto font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.7)' }}>
              Nos experts sont à votre disposition pour vous conseiller et vous accompagner.
            </p>
            <Link to="/contact" className="group relative inline-flex items-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden" style={COPPER_BTN_STYLE} data-testid="savoir-cta-btn">
              <Shimmer />
              <span className="relative">Discutons-en</span>
              <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </div>
        </div>
      </section>
    </VitrineLayout>
  );
}
